// Demo numbering, as ledger.numbering and ledger.set_numbering (db/031, #13):
// the same rules — never backwards, never a number already used with that prefix.
import { demoDocumentNumbering, demoNumbersUsed } from './mockDocuments';
import { demoJournalNumbering, demoJournalNumbersUsed } from './mockLedger';
import type { LedgerSource, NumberSequence } from './source';

type Part = Pick<LedgerSource, 'numbering' | 'setNumbering'>;
type Counter = { prefix: string; next_value: number; digits: number };
const delay = <T,>(v: T, ms = 80): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));
const fail = (m: string) => Promise.reject(new Error(m));

const KINDS: { key: NumberSequence['key']; label: string }[] = [
  { key: 'sales_invoice', label: 'Invoices' }, { key: 'sales_credit', label: 'Credit notes' }, { key: 'purchase_bill', label: 'Bills' },
  { key: 'purchase_credit', label: 'Supplier credit notes' }, { key: 'journal', label: 'Journals' },
];

export const formatNumber = (prefix: string, value: number, digits: number) => `${prefix}${String(value).padStart(digits, '0')}`;

const counter = (key: string): Counter => (key === 'journal' ? demoJournalNumbering() : demoDocumentNumbering()[key]!);
const lastUsed = (key: string, prefix: string) => {
  const used = key === 'journal' ? demoJournalNumbersUsed(prefix) : demoNumbersUsed(key, prefix);
  return used.length ? Math.max(...used) : null;
};

const list = (): NumberSequence[] => KINDS.map(({ key, label }) => {
  const c = counter(key);
  return { key, label, prefix: c.prefix, next_value: c.next_value, digits: c.digits, next: formatNumber(c.prefix, c.next_value, c.digits), last_used: lastUsed(key, c.prefix) };
});

export const demoNumbering: Part = {
  numbering: () => delay(list()),
  setNumbering: (patch) => {
    for (const k of Object.keys(patch)) if (!KINDS.some((x) => x.key === k)) return fail(`${k} isn’t a kind of document that is numbered here`);
    const changes: [Counter, Counter][] = [];
    for (const { key, label } of KINDS) {
      const n = patch[key];
      if (!n) continue;
      const c = counter(key);
      const next = { prefix: n.prefix ?? c.prefix, next_value: n.next_value ?? c.next_value, digits: n.digits ?? c.digits };
      if (!/^[A-Za-z0-9/_.-]{0,10}$/.test(next.prefix)) return fail(`${label}: a prefix is up to 10 letters, digits and - / _ . (such as INV- or SI)`);
      if (!(next.digits >= 1 && next.digits <= 10)) return fail(`${label}: the number is 1 to 10 digits long`);
      if (!(next.next_value >= 1 && next.next_value <= 9999999999)) return fail(`${label}: the next number is between 1 and 9999999999`);
      if (next.next_value < c.next_value) return fail(`${label}: numbers can’t go backwards; the next is already ${formatNumber(c.prefix, c.next_value, c.digits)}`);
      const last = lastUsed(key, next.prefix);
      if (last !== null && next.next_value <= last) return fail(`${label}: ${formatNumber(next.prefix, last, next.digits)} has already been used; start after it`);
      changes.push([c, next]);
    }
    for (const [c, next] of changes) Object.assign(c, next);
    return delay(list());
  },
};
